import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, Users } from 'lucide-react'

import type { Employee } from '@/lib/api/employee-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { RowDeleteButton } from '@/components/common/row-delete-button'
import { EmployeeFormDialog } from '@/components/employees/employee-form-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDebounced } from '@/hooks/use-debounced'
import { useAllDepartments } from '@/hooks/use-department-query'
import { useDeleteEmployee, useEmployees } from '@/hooks/use-employee-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/organization/employees/',
)({
    component: EmployeesPage,
})

function EmployeesPage() {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)
    const [page, setPage] = useState(1)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editing, setEditing] = useState<Employee | undefined>()

    const { data, isLoading } = useEmployees({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        page,
        per_page: 20,
    })
    const employees = useMemo(() => data?.data ?? [], [data])
    const totalPages = data?.meta?.total_pages ?? 1

    // Ô chọn phòng ban trong form: cả cây, cha lẫn con, đã lật hết trang.
    const { data: deptData } = useAllDepartments()
    const departments = useMemo(() => deptData ?? [], [deptData])

    const remove = useDeleteEmployee()

    const openCreate = () => {
        setEditing(undefined)
        setDialogOpen(true)
    }
    const openEdit = (e: Employee) => {
        setEditing(e)
        setDialogOpen(true)
    }

    return (
        <div className="space-y-4 p-4">
            <ListPageHeader
                icon={<Users className="size-6" />}
                title={m.emp_title()}
                subtitle={m.emp_subtitle()}
                primaryActions={
                    <Button onClick={openCreate}>
                        <Plus className="size-4" />
                        {m.emp_add()}
                    </Button>
                }
            />

            <Input
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value)
                    // Gõ từ khoá mới mà vẫn ở trang 5 thì bảng trông như rỗng.
                    setPage(1)
                }}
                placeholder={m.emp_search_placeholder()}
                aria-label={m.emp_search_placeholder()}
                className="max-w-sm"
            />

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.emp_col_name()}</TableHead>
                            <TableHead>{m.emp_col_phone()}</TableHead>
                            <TableHead>{m.emp_col_address()}</TableHead>
                            <TableHead>{m.emp_col_department()}</TableHead>
                            <TableHead>{m.emp_col_user()}</TableHead>
                            <TableHead className="w-24" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {employees.map((e) => (
                            <TableRow key={e.id}>
                                <TableCell className="font-medium">
                                    {e.full_name}
                                </TableCell>
                                <TableCell className="tabular-nums">
                                    {e.phone || '—'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {e.address || '—'}
                                </TableCell>
                                <TableCell>
                                    {e.department_name || (
                                        <span className="text-muted-foreground">
                                            {m.emp_department_none()}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {e.user_username || (
                                        <span className="text-muted-foreground">
                                            {m.emp_user_none()}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex justify-end gap-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => openEdit(e)}
                                        >
                                            {m.common_edit()}
                                        </Button>
                                        <RowDeleteButton
                                            label={m.common_delete()}
                                            confirmMessage={m.emp_delete_confirm()}
                                            pending={remove.isPending}
                                            onDelete={() => remove.mutate(e.id)}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!isLoading && employees.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.emp_empty()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        {m.common_prev()}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                        {page}/{totalPages}
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {m.common_next()}
                    </Button>
                </div>
            )}

            <EmployeeFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                employee={editing}
                departments={departments}
            />
        </div>
    )
}
